"use client";
import {
  type ISchedule,
  type IUserFit,
  type OfficeWithFloors,
  parseTimeOnly
} from "@gettactic/api";
import { getDateFromSchedule, getTimeFromSchedule } from "@gettactic/helpers";
import { format } from "date-fns";
import { useMemo } from "react";
import {
  getScheduleTimeline,
  getUsersFromSchedule,
  parseScheduleDate
} from "../globalSearchScheduleUtils";

/**
 * Resolve the resource a schedule occupies. Desk schedules from the core
 * API can carry the resource on the slot instead of the schedule root.
 */
const getScheduleResource = (schedule: ISchedule) =>
  schedule.resource ??
  schedule.slots?.find((s) => s.resource)?.resource ??
  null;

export interface CurrentScheduleInfo {
  type: "current";
  endTime: string;
  freeUntilText: string;
  users: IUserFit[];
  isMeetingRoom: boolean;
  meetingTitle?: string | null;
}

export interface UpcomingScheduleInfo {
  type: "upcoming";
  text: string;
}

/** Every matched schedule already ended — the resource may be free now.
 *  Callers should only surface this alongside an unavailable state, never
 *  next to an "Available" indicator. */
export interface PastScheduleInfo {
  type: "past";
  text: string;
}

export type ResourceScheduleInfo =
  | CurrentScheduleInfo
  | UpcomingScheduleInfo
  | PastScheduleInfo
  | null;

export function useResourceScheduleInfo(
  schedules: ISchedule[] | undefined,
  office?: OfficeWithFloors
): ResourceScheduleInfo {
  return useMemo(() => {
    // The office is not part of the computation — requiring it hid the
    // occupancy line whenever the divisions lookup didn't resolve the
    // office (it only covers offices assigned to divisions).
    if (!schedules || schedules.length === 0) return null;

    const { currentSchedule, upcomingSchedule: nextSchedule } =
      getScheduleTimeline(schedules);

    if (currentSchedule) {
      const endTime = getTimeFromSchedule(currentSchedule.end);
      // Desk schedules from global search can carry the resource on the
      // slot only — never assume the schedule root has one.
      const isMeetingRoom =
        getScheduleResource(currentSchedule)?.type === "meeting_room";
      const currentScheduleUsers = getUsersFromSchedule(currentSchedule);

      let freeUntilText = "";
      if (nextSchedule) {
        const nextStartTime = getTimeFromSchedule(nextSchedule.start);
        freeUntilText = `will be free until ${nextStartTime}`;
      } else {
        freeUntilText = "will be free";
      }

      return {
        type: "current",
        endTime,
        freeUntilText,
        users: currentScheduleUsers,
        isMeetingRoom,
        meetingTitle: isMeetingRoom ? currentSchedule.title : undefined
      };
    }

    if (nextSchedule) {
      const formattedTime = getTimeFromSchedule(nextSchedule.start);
      return {
        type: "upcoming",
        text: `Will be occupied at ${formattedTime}`
      };
    }

    // Neither current nor upcoming relative to the wall clock — every
    // matched schedule has already ended (a spanning one would classify
    // as current). Still describe the occupancy for the searched window,
    // but in the past tense: the desk may be free right now. Pick the
    // earliest by start time — the array order is not time-sorted.
    const windowSchedule = [...schedules].sort(
      (a, b) =>
        parseScheduleDate(a.start).getTime() -
        parseScheduleDate(b.start).getTime()
    )[0];
    if (windowSchedule) {
      const startTime = getTimeFromSchedule(windowSchedule.start);
      const endTime = getTimeFromSchedule(windowSchedule.end);
      return {
        type: "past",
        text: `Was occupied ${startTime} – ${endTime}`
      };
    }

    return null;
  }, [schedules, office]);
}

export interface PersonScheduleInfo {
  type: "current" | "upcoming" | "past";
  schedule: ISchedule;
  text: string;
  resourceId: string;
  resourceName: string;
  officeName?: string;
  floorName?: string;
}

export function usePersonScheduleInfo(
  schedules: ISchedule[] | undefined,
  getOfficeFloorInfo: (
    officeId?: string | null,
    parentId?: string | null
  ) => {
    officeName?: string;
    floorName?: string;
  }
): PersonScheduleInfo | null {
  return useMemo(() => {
    return getScheduleInfo({ schedules, getOfficeFloorInfo });
  }, [schedules, getOfficeFloorInfo]);
}

export const getScheduleInfo = ({
  schedules,
  getOfficeFloorInfo
}: {
  schedules: ISchedule[] | undefined;
  getOfficeFloorInfo: (
    officeId?: string | null,
    parentId?: string | null
  ) => {
    officeName?: string;
    floorName?: string;
  };
}): PersonScheduleInfo | null => {
  if (!schedules || schedules.length === 0) return null;

  const { now, currentSchedule, upcomingSchedule, pastSchedule } =
    getScheduleTimeline(schedules);

  if (currentSchedule) {
    const currentResource = getScheduleResource(currentSchedule);
    if (currentResource) {
      const { officeName, floorName } = getOfficeFloorInfo(
        currentResource.office_id,
        currentResource.parent_id
      );
      const resourceName = currentResource.name;

      return {
        type: "current",
        schedule: currentSchedule,
        resourceName,
        officeName,
        floorName,
        text: `now at ${resourceName}${officeName ? ` (${officeName})` : ""}`,
        resourceId: currentResource.id
      };
    }
    // No resolvable resource on the schedule — the person is still in
    // the office NOW; degrading to an upcoming/past line would misstate
    // their presence.
    return {
      type: "current",
      schedule: currentSchedule,
      resourceName: "",
      officeName: undefined,
      floorName: undefined,
      text: "now in the office",
      resourceId: ""
    };
  }

  const upcomingResource = upcomingSchedule
    ? getScheduleResource(upcomingSchedule)
    : null;
  if (upcomingSchedule && upcomingResource) {
    const { officeName, floorName } = getOfficeFloorInfo(
      upcomingResource.office_id,
      upcomingResource.parent_id
    );

    const startDateInOfficeTz = getDateFromSchedule(upcomingSchedule.start);

    const nowInOfficeTz = format(now, "yyyy-MM-dd");
    const isToday = startDateInOfficeTz === nowInOfficeTz;

    const timeStr = `${parseTimeOnly(upcomingSchedule.start)} → ${parseTimeOnly(upcomingSchedule.end)}`;

    return {
      type: "upcoming",
      schedule: upcomingSchedule,
      text: `will be in the office ${isToday ? "at" : "on"} ${timeStr}`,
      resourceId: upcomingResource.id,
      resourceName: upcomingResource.name,
      officeName,
      floorName
    };
  }

  const pastResource = pastSchedule ? getScheduleResource(pastSchedule) : null;
  if (pastSchedule && pastResource) {
    const { officeName, floorName } = getOfficeFloorInfo(
      pastResource.office_id,
      pastResource.parent_id
    );

    // Same trailing-Z-stripping parse the timeline classification uses —
    // plain new Date() would shift the shown day/times by the timezone
    // offset relative to how the schedule was bucketed.
    const endDate = parseScheduleDate(pastSchedule.end);
    const endInOfficeTz = format(endDate, "yyyy-MM-dd");
    const nowInOfficeTz = format(now, "yyyy-MM-dd");
    const isToday = endInOfficeTz === nowInOfficeTz;

    const startTime = format(parseScheduleDate(pastSchedule.start), "p");
    const endTime = format(endDate, "p");
    const timeStr = `${startTime} → ${endTime}`;

    return {
      type: "past",
      schedule: pastSchedule,
      text: `was in the office ${isToday ? "at" : "on"} ${timeStr}`,
      resourceName: pastResource.name,
      resourceId: pastResource.id,
      officeName,
      floorName
    };
  }

  return null;
};

export function usePersonStatus(
  scheduleInfo: PersonScheduleInfo | null
): "in-office" | "in-meeting" | "remote" | "offline" {
  return useMemo(() => {
    if (scheduleInfo?.type === "current" && scheduleInfo.schedule) {
      const currentSchedule = scheduleInfo.schedule;

      if (currentSchedule.is_virtual || currentSchedule.online_meeting) {
        return "in-meeting";
      }

      switch (currentSchedule.presence_type) {
        case "in_person":
          return "in-office";
        case "remote":
          return "remote";
        case "out_of_office":
          return "offline";
        default:
          if (currentSchedule.presence_status === "accepted") {
            return "in-office";
          }
          return "offline";
      }
    }

    return "offline";
  }, [scheduleInfo]);
}
